import {
  LEGACY_OPEN_SESSION_KEY,
  OPEN_SESSION_KEY_PREFIX,
  parseOpenSessionRecord,
  shouldKeepOpenSessionKey,
  shouldReportUncleanExit,
  type OpenSessionRecord,
} from './lib/reliability/openSessionSweep'
import {
  BREADCRUMBS_VERSION,
  createBreadcrumbTrail,
  formatClickDetail,
  formatFetchFailDetail,
} from './lib/reliability/breadcrumbs'
import { SNAPSHOT_REDACTED, sanitizeSnapshotHtml } from './lib/reliability/snapshotSanitizer'

type ReliabilitySeverity = 'debug' | 'info' | 'warn' | 'error' | 'crash'

type ReliabilityPayload = Record<string, unknown>

type ReliabilityEvent = {
  eventType: string
  severity?: ReliabilitySeverity
  payload?: ReliabilityPayload
  beacon?: boolean
}

// Open-session tracking is PER SESSION (`har_reliability_open_session:<sessionId>`).
// The old single shared key logged a false session_recovered_after_unclean_exit on
// every reload while a second tab was open — see src/lib/reliability/openSessionSweep.ts.
const SESSION_STORAGE_KEY = 'har_reliability_session_id'
const HEARTBEAT_MS = 30_000
const MAX_DIAGNOSTIC_TEXT = 1_500
const MAX_CONSOLE_ARGS = 8
const MAX_CONSOLE_BREADCRUMBS = 20
const MAX_CONSOLE_EVENTS_PER_MINUTE = 60
const MAX_LONG_TASK_EVENTS_PER_MINUTE = 20
const LONG_TASK_MIN_DURATION_MS = 250
const REDACTED = '[redacted]'
const SENSITIVE_KEY_RE = /(token|secret|password|passcode|authorization|cookie|jwt|session|apikey|api_key|access[_-]?key|refresh)/i
const SENSITIVE_VALUE_RE = /\b(Bearer\s+)[A-Za-z0-9._~+/=-]+|(?:CF_Authorization|Authorization|access_token|refresh_token|id_token|client_secret)=([^;&\s]+)/gi
const IGNORED_CONSOLE_RE = /(bootstrap-autofill-overlay|chrome-extension:\/\/|edge-extension:\/\/|moz-extension:\/\/|safari-web-extension:\/\/|static\.cloudflareinsights\.com\/beacon|net::ERR_BLOCKED_BY_CLIENT|Tracking Prevention blocked|Fetch finished loading)/i

let initialized = false
let sessionId = ''
let sequence = 0
let heartbeatTimer: number | null = null
let consoleDiagnosticsInstalled = false
let consoleWindowStartedAt = 0
let consoleWindowCount = 0
let longTaskWindowStartedAt = 0
let longTaskWindowCount = 0

type ConsoleBreadcrumb = {
  level: 'warn' | 'error'
  at: string
  route: string
  summary: string
}

const consoleBreadcrumbs: ConsoleBreadcrumb[] = []

// err-breadcrumbs-v1 — WHO/WHERE/WHAT action trail for the error-ticket
// pipeline. Contract shared with the /api/client-events ingest + error_tickets:
// crash + console_error POST bodies carry TOP-LEVEL additive fields
//   breadcrumbs:    Array<{ t, kind: 'route'|'click'|'fetch-fail'|'visibility', detail<=120ch }> (last 25)
//   snapshot_html?: string   (severity=crash ONLY; sanitized; <=150KB)
//   snapshot_meta?: { route, title, viewport:{width,height}, ua }
// They ride the body ROOT — NOT payload — because the ingest caps payload_json
// at 24KB (a snapshot inside payload would truncate/corrupt the stored JSON).
// The legacy payload.breadcrumbs (console summaries) is unchanged.
const actionTrail = createBreadcrumbTrail()
const TRAIL_EVENT_TYPES = new Set(['crash', 'console_error'])
let actionBreadcrumbSourcesInstalled = false
let stashedCrashSnapshot: { html: string | undefined; meta: ReturnType<typeof snapshotMeta>; at: number } | null = null

function newSessionId(): string {
  try {
    const id = crypto.randomUUID()
    sessionStorage.setItem(SESSION_STORAGE_KEY, id)
    return id
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

function routeSnapshot() {
  const hash = window.location.hash || '#/'
  const activeView = hash.replace(/^#\/?/, '').split(/[/?]/)[0] || 'command'
  return {
    href: window.location.href,
    route: hash,
    activeView,
    visibility: document.visibilityState,
  }
}

function memorySnapshot() {
  const memory = (performance as Performance & {
    memory?: {
      usedJSHeapSize?: number
      totalJSHeapSize?: number
      jsHeapSizeLimit?: number
    }
  }).memory
  if (!memory) return {}
  return {
    heapUsed: Math.round(memory.usedJSHeapSize || 0),
    heapTotal: Math.round(memory.totalJSHeapSize || 0),
    heapLimit: Math.round(memory.jsHeapSizeLimit || 0),
  }
}

function environmentSnapshot() {
  const nav = navigator as Navigator & {
    connection?: {
      effectiveType?: string
      downlink?: number
      rtt?: number
      saveData?: boolean
    }
  }
  const navigationEntry = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined
  return {
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    },
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
      colorDepth: window.screen?.colorDepth,
    },
    page: {
      title: document.title,
      referrer: document.referrer || null,
      online: navigator.onLine,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeOrigin: Math.round(performance.timeOrigin || 0),
      nowMs: Math.round(performance.now()),
    },
    connection: nav.connection ? {
      effectiveType: nav.connection.effectiveType,
      downlink: nav.connection.downlink,
      rtt: nav.connection.rtt,
      saveData: nav.connection.saveData,
    } : null,
    navigation: navigationEntry ? {
      type: navigationEntry.type,
      domContentLoadedMs: Math.round(navigationEntry.domContentLoadedEventEnd),
      loadEventMs: Math.round(navigationEntry.loadEventEnd),
      transferSize: navigationEntry.transferSize,
      encodedBodySize: navigationEntry.encodedBodySize,
      decodedBodySize: navigationEntry.decodedBodySize,
    } : null,
  }
}

function redactText(input: string) {
  return input
    .replace(SENSITIVE_VALUE_RE, (_match, bearerPrefix: string | undefined) => bearerPrefix ? `${bearerPrefix}${REDACTED}` : REDACTED)
    .slice(0, MAX_DIAGNOSTIC_TEXT)
}

function safeStringify(value: unknown) {
  try {
    return JSON.stringify(value)
  } catch {
    return Object.prototype.toString.call(value)
  }
}

function serializeConsoleArg(value: unknown, depth = 0): unknown {
  if (value == null) return value
  if (value instanceof Error) return describeError(value)
  if (value instanceof Event) {
    return {
      eventType: value.type,
      target: value.target instanceof Element ? {
        tag: value.target.tagName,
        id: value.target.id || undefined,
        className: typeof value.target.className === 'string' ? value.target.className.slice(0, 200) : undefined,
      } : undefined,
    }
  }
  if (value instanceof Response) {
    return { response: { url: redactText(value.url), status: value.status, statusText: value.statusText, type: value.type, redirected: value.redirected } }
  }
  if (value instanceof Request) {
    return { request: { url: redactText(value.url), method: value.method, mode: value.mode, credentials: value.credentials } }
  }
  if (typeof value === 'string') return redactText(value)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (typeof value === 'function') return `[function ${value.name || 'anonymous'}]`
  if (typeof value !== 'object') return redactText(String(value))
  if (depth >= 2) return redactText(safeStringify(value))

  const out: Record<string, unknown> = {}
  for (const [key, inner] of Object.entries(value as Record<string, unknown>).slice(0, 30)) {
    out[key] = SENSITIVE_KEY_RE.test(key) ? REDACTED : serializeConsoleArg(inner, depth + 1)
  }
  return out
}

function consoleSummary(args: unknown[]) {
  return args
    .slice(0, 3)
    .map((arg) => {
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`
      if (typeof arg === 'string') return redactText(arg)
      return redactText(safeStringify(serializeConsoleArg(arg)))
    })
    .join(' ')
    .slice(0, 300)
}

function canSendConsoleEvent() {
  const now = Date.now()
  if (now - consoleWindowStartedAt > 60_000) {
    consoleWindowStartedAt = now
    consoleWindowCount = 0
  }
  consoleWindowCount += 1
  return consoleWindowCount <= MAX_CONSOLE_EVENTS_PER_MINUTE
}

function canSendLongTaskEvent() {
  const now = Date.now()
  if (now - longTaskWindowStartedAt > 60_000) {
    longTaskWindowStartedAt = now
    longTaskWindowCount = 0
  }
  longTaskWindowCount += 1
  return longTaskWindowCount <= MAX_LONG_TASK_EVENTS_PER_MINUTE
}

function recordConsoleBreadcrumb(level: 'warn' | 'error', args: unknown[]) {
  consoleBreadcrumbs.push({
    level,
    at: new Date().toISOString(),
    route: window.location.hash || '#/',
    summary: consoleSummary(args),
  })
  if (consoleBreadcrumbs.length > MAX_CONSOLE_BREADCRUMBS) consoleBreadcrumbs.shift()
}

function shouldIgnoreConsoleDiagnostic(summary: string) {
  return IGNORED_CONSOLE_RE.test(summary)
}

function installConsoleDiagnostics() {
  if (consoleDiagnosticsInstalled) return
  consoleDiagnosticsInstalled = true

  const originalError = console.error.bind(console)
  const originalWarn = console.warn.bind(console)

  const capture = (level: 'warn' | 'error', args: unknown[]) => {
    try {
      const summary = consoleSummary(args)
      if (shouldIgnoreConsoleDiagnostic(summary)) return
      recordConsoleBreadcrumb(level, args)
      if (!canSendConsoleEvent()) return
      reportClientEvent(`console_${level}`, {
        level,
        args: args.slice(0, MAX_CONSOLE_ARGS).map((arg) => serializeConsoleArg(arg)),
        summary,
        capturedStack: describeError(new Error(`console.${level}`)).stack,
        breadcrumbs: consoleBreadcrumbs.slice(-MAX_CONSOLE_BREADCRUMBS),
        environment: environmentSnapshot(),
      }, level === 'error' ? 'error' : 'warn', level === 'error')
    } catch { /* diagnostics must never break console */ }
  }

  console.error = (...args: unknown[]) => {
    originalError(...args)
    capture('error', args)
  }
  console.warn = (...args: unknown[]) => {
    originalWarn(...args)
    capture('warn', args)
  }
}

function installLongTaskDiagnostics() {
  const perfObserver = (window as unknown as Window & {
    PerformanceObserver?: typeof PerformanceObserver & { supportedEntryTypes?: string[] }
  }).PerformanceObserver
  if (!perfObserver) return
  if (Array.isArray(perfObserver.supportedEntryTypes) && !perfObserver.supportedEntryTypes.includes('longtask')) return

  try {
    const observer = new perfObserver((list) => {
      for (const entry of list.getEntries()) {
        const durationMs = Math.round(entry.duration || 0)
        if (durationMs < LONG_TASK_MIN_DURATION_MS) continue
        if (!canSendLongTaskEvent()) break
        const longTask = entry as PerformanceEntry & {
          attribution?: Array<Record<string, unknown>>
        }
        reportClientEvent('long_task', {
          name: longTask.name,
          entryType: longTask.entryType,
          startTimeMs: Math.round(longTask.startTime || 0),
          durationMs,
          attribution: Array.isArray(longTask.attribution)
            ? longTask.attribution.slice(0, 5).map((item) => serializeConsoleArg(item))
            : undefined,
          breadcrumbs: consoleBreadcrumbs.slice(-MAX_CONSOLE_BREADCRUMBS),
        }, durationMs >= 500 ? 'warn' : 'info')
      }
    })
    observer.observe({ type: 'longtask', buffered: true } as PerformanceObserverInit)
  } catch { /* unsupported browsers or privacy modes */ }
}

const CLICKABLE_SELECTOR = 'button, a, input, select, textarea, label, summary, [role="button"], [role="tab"], [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], [role="option"], [role="checkbox"], [role="switch"], [contenteditable]'
const VALUE_BEARING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

// Compact click description for the action trail. NEVER reads .value,
// contenteditable text, or [data-sensitive] subtrees — value-bearing elements
// are labelled from static attributes only. Non-actionable targets get a bare
// selector (no textContent: page-level containers hold the whole page's text).
function describeClickTarget(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null
  const actionable = target.closest(CLICKABLE_SELECTOR)
  const el = actionable || target
  const valueBearing = VALUE_BEARING_TAGS.has(el.tagName) || (el instanceof HTMLElement && el.isContentEditable)
  let text = ''
  if (valueBearing) {
    text = el.getAttribute('aria-label') || el.getAttribute('placeholder') || el.getAttribute('name') || el.getAttribute('type') || ''
  } else if (actionable && !el.closest('[data-sensitive]')) {
    text = el.textContent || ''
  }
  return formatClickDetail({
    tag: el.tagName,
    id: el.id || '',
    className: el.getAttribute('class') || '',
    text,
  })
}

function installActionBreadcrumbSources() {
  if (actionBreadcrumbSourcesInstalled) return
  actionBreadcrumbSourcesInstalled = true

  document.addEventListener('click', (event) => {
    try {
      const detail = describeClickTarget(event.target)
      if (detail) actionTrail.record('click', detail)
    } catch { /* breadcrumbs must never break the app */ }
  }, { capture: true, passive: true })

  // Failed-fetch breadcrumbs: tap outcomes only. The ORIGINAL promise is
  // returned untouched (no body reads, no arg rewrites, aborts pass through),
  // and the telemetry channel itself is never self-traced.
  if (typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window)
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
      let url = ''
      let method = 'GET'
      try {
        if (typeof input === 'string') url = input
        else if (input instanceof Request) { url = input.url; method = input.method || 'GET' }
        else if (input instanceof URL) url = input.href
        else url = String(input)
        if (init?.method) method = init.method
      } catch { /* describing the request must never affect it */ }
      const result = originalFetch(input as RequestInfo, init)
      if (url && !url.includes('/api/client-events')) {
        result.then(
          (response) => {
            try {
              if (response && response.status >= 400) actionTrail.record('fetch-fail', formatFetchFailDetail(method, url, String(response.status)))
            } catch { /* best effort */ }
          },
          (reason) => {
            try {
              // Deliberate cancellations (route/filter-change aborts) are not failures.
              if (!(reason instanceof Error && reason.name === 'AbortError')) {
                actionTrail.record('fetch-fail', formatFetchFailDetail(method, url, 'network'))
              }
            } catch { /* best effort */ }
          },
        )
      }
      return result
    }) as typeof window.fetch
  }
}

function persistOpenSession(closedAt?: string) {
  try {
    localStorage.setItem(OPEN_SESSION_KEY_PREFIX + sessionId, JSON.stringify({
      sessionId,
      heartbeatAt: new Date().toISOString(),
      closedAt: closedAt || null,
      ...routeSnapshot(),
    }))
  } catch { /* storage can be blocked */ }
}

// Sweep persisted open-session records (per-session keys + the retired shared key).
// Deletes closed/stale/invalid records; returns the ones that indicate a genuinely
// unclean exit (open + heartbeat >90s stale). Fresh-open records are LEFT ALONE —
// they belong to live sibling tabs that will close their own key on pagehide.
function sweepOpenSessionRecords(nowIso: string): OpenSessionRecord[] {
  const unclean: OpenSessionRecord[] = []
  try {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      if (key === LEGACY_OPEN_SESSION_KEY || key.startsWith(OPEN_SESSION_KEY_PREFIX)) keys.push(key)
    }
    for (const key of keys) {
      if (key === OPEN_SESSION_KEY_PREFIX + sessionId) continue // never sweep our own key
      const record = parseOpenSessionRecord(localStorage.getItem(key))
      if (shouldKeepOpenSessionKey(record, nowIso)) continue
      if (record && shouldReportUncleanExit(record, nowIso)) unclean.push(record)
      localStorage.removeItem(key)
    }
  } catch { /* storage can be blocked */ }
  return unclean
}

// Renderer-native (Blink-side) footprint — detached-DOM / resource accumulation is
// invisible to usedJSHeapSize, so surface DOM node + resource-timing counts in every
// heartbeat. This is the instrumentation that makes the next idle-tab renderer death
// attributable (app-side native growth vs a browser-side idle kill).
function rendererFootprintSnapshot() {
  try {
    return {
      domNodes: document.getElementsByTagName('*').length,
      resourceEntries: performance.getEntriesByType?.('resource')?.length ?? 0,
    }
  } catch {
    return {}
  }
}

function makeEvent(input: ReliabilityEvent) {
  const event: Record<string, unknown> = {
    sessionId,
    sequence: ++sequence,
    eventType: input.eventType,
    severity: input.severity || 'info',
    occurredAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    ...routeSnapshot(),
    ...memorySnapshot(),
    payload: input.payload || {},
  }
  // err-breadcrumbs-v1: crash + console_error events carry the action trail
  // as a TOP-LEVEL additive field (see contract note above actionTrail).
  if (TRAIL_EVENT_TYPES.has(input.eventType)) event.breadcrumbs = actionTrail.list()
  return event
}

function postEventBody(event: Record<string, unknown>, beacon: boolean) {
  const body = JSON.stringify(event)
  try {
    if (beacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon('/api/client-events', blob)
      return
    }
    fetch('/api/client-events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: body.length < 60_000,
      body,
    }).catch(() => { /* best effort */ })
  } catch { /* best effort */ }
}

export function reportClientEvent(eventType: string, payload?: ReliabilityPayload, severity: ReliabilitySeverity = 'info', beacon = false) {
  if (typeof window === 'undefined') return
  if (!sessionId) sessionId = newSessionId()
  postEventBody(makeEvent({ eventType, payload, severity, beacon }), beacon)
}

function snapshotMeta() {
  return {
    route: window.location.hash || '#/',
    title: (document.title || '').slice(0, 200),
    viewport: { width: window.innerWidth, height: window.innerHeight },
    ua: (navigator.userAgent || '').slice(0, 400),
  }
}

// Sanitized DOM snapshot: DOM-side scrub on a CLONE (scripts out; input values
// + checked/selected state + [data-sensitive] subtrees redacted — handles
// nesting reliably), then the pure string sanitizer as the final authority
// (inline handlers, javascript: URLs, value attrs, auth material, 150KB cap).
// Never throws; undefined when the DOM is unavailable.
function buildSanitizedSnapshot(): string | undefined {
  try {
    const root = document.documentElement
    if (!root) return undefined
    const clone = root.cloneNode(true) as HTMLElement
    clone.querySelectorAll('script').forEach((el) => el.remove())
    clone.querySelectorAll('input').forEach((el) => {
      el.setAttribute('value', SNAPSHOT_REDACTED)
      el.removeAttribute('checked')
    })
    clone.querySelectorAll('textarea').forEach((el) => { el.textContent = SNAPSHOT_REDACTED })
    clone.querySelectorAll('option').forEach((el) => el.removeAttribute('selected'))
    clone.querySelectorAll('[data-sensitive]').forEach((el) => { el.textContent = SNAPSHOT_REDACTED })
    return sanitizeSnapshotHtml(clone.outerHTML)
  } catch {
    return undefined
  }
}

// Called from AppErrorBoundary.getDerivedStateFromError — the LAST moment the
// crashed page's DOM is still committed. By componentDidCatch, React has
// already swapped in the fallback UI, so capturing there would snapshot the
// "Something went wrong" screen instead of what the user was looking at.
// Read-only DOM serialization: safe during the render phase and idempotent
// under StrictMode double-invocation.
export function captureCrashSnapshot() {
  try {
    stashedCrashSnapshot = {
      html: buildSanitizedSnapshot(),
      meta: snapshotMeta(),
      at: Date.now(),
    }
  } catch {
    stashedCrashSnapshot = null
  }
}

// Called from AppErrorBoundary.componentDidCatch IN ADDITION to the existing
// console.error('App crashed:') mirror (which stays — existing dashboards and
// forensics key on it). Emits the error-ticket contract event: eventType
// 'crash', severity 'crash', with top-level breadcrumbs + snapshot_html +
// snapshot_meta. Sent via fetch, never sendBeacon — beacons silently drop
// >64KB payloads and a snapshot can be 150KB; the error boundary's fallback
// UI keeps the page alive long enough for fetch to complete.
export function reportAppCrash(error: unknown, info?: { componentStack?: string | null }) {
  if (typeof window === 'undefined') return
  try {
    if (!sessionId) sessionId = newSessionId()
    const fresh = stashedCrashSnapshot && Date.now() - stashedCrashSnapshot.at < 30_000 ? stashedCrashSnapshot : null
    stashedCrashSnapshot = null
    const event = makeEvent({
      eventType: 'crash',
      severity: 'crash',
      payload: {
        ...describeError(error),
        componentStack: typeof info?.componentStack === 'string' ? redactText(info.componentStack) : undefined,
        captureVersion: BREADCRUMBS_VERSION,
        breadcrumbs: consoleBreadcrumbs.slice(-MAX_CONSOLE_BREADCRUMBS),
        environment: environmentSnapshot(),
      },
    })
    event.snapshot_html = fresh ? fresh.html : buildSanitizedSnapshot()
    event.snapshot_meta = fresh ? fresh.meta : snapshotMeta()
    postEventBody(event, false)
  } catch { /* crash reporting must never break the crash path */ }
}

export function initClientReliability() {
  if (typeof window === 'undefined' || initialized) return
  initialized = true
  sessionId = newSessionId()
  installConsoleDiagnostics()
  installLongTaskDiagnostics()
  installActionBreadcrumbSources()
  actionTrail.record('route', window.location.hash || '#/')

  const uncleanSessions = sweepOpenSessionRecords(new Date().toISOString())
  persistOpenSession()

  reportClientEvent('session_start', {
    previousSessionUnclean: uncleanSessions.length > 0,
    previousSession: uncleanSessions[0] ? {
      sessionId: uncleanSessions[0].sessionId,
      heartbeatAt: uncleanSessions[0].heartbeatAt,
      route: uncleanSessions[0].route,
      activeView: uncleanSessions[0].activeView,
    } : undefined,
  })

  // One event per genuinely dead session (open + stale heartbeat). NOTE for
  // dashboards: 2026-07-01 removed the shared-key false positives (every clean
  // reload with a second tab open used to misfire) — expect a step-change DROP
  // in session_recovered_after_unclean_exit volume from this date.
  for (const previous of uncleanSessions) {
    reportClientEvent('session_recovered_after_unclean_exit', {
      previousSessionId: previous.sessionId,
      previousHeartbeatAt: previous.heartbeatAt,
      previousRoute: previous.route,
      previousActiveView: previous.activeView,
    }, 'warn')
  }

  heartbeatTimer = window.setInterval(() => {
    persistOpenSession()
    if (document.visibilityState === 'visible') reportClientEvent('heartbeat', rendererFootprintSnapshot())
  }, HEARTBEAT_MS)

  window.addEventListener('hashchange', () => {
    persistOpenSession()
    actionTrail.record('route', window.location.hash || '#/')
    reportClientEvent('route_change')
  })

  document.addEventListener('visibilitychange', () => {
    persistOpenSession()
    actionTrail.record('visibility', document.visibilityState)
    reportClientEvent('visibility_change', { visibility: document.visibilityState })
  })

  window.addEventListener('pagehide', () => {
    const closedAt = new Date().toISOString()
    persistOpenSession(closedAt)
    reportClientEvent('session_end', { closedAt }, 'info', true)
    if (heartbeatTimer) window.clearInterval(heartbeatTimer)
  })
}

/** Read-only copy of the err-breadcrumbs-v1 action trail (last 25 route/
 *  click/fetch-fail/visibility crumbs). Used by the Support Hub so a USER-
 *  reported issue (marker: support-hub-modal) carries the same "what they
 *  were doing" context as an auto-captured crash ticket. Copies only —
 *  callers can never mutate the live ring. */
export function listActionBreadcrumbs() {
  return actionTrail.list()
}

export function describeError(value: unknown) {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack }
  }
  if (typeof value === 'object' && value !== null) {
    const maybe = value as { message?: unknown; stack?: unknown; name?: unknown }
    return {
      name: typeof maybe.name === 'string' ? maybe.name : undefined,
      message: typeof maybe.message === 'string' ? maybe.message : String(value),
      stack: typeof maybe.stack === 'string' ? maybe.stack : undefined,
    }
  }
  return { message: String(value) }
}
